<template>
  <transition name="fade">
    <div v-if="show" class="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm"></div>
  </transition>

  <transition name="slide-up">
    <div v-if="show" class="fixed inset-x-0 bottom-0 z-[110] flex justify-center pointer-events-none">
      <div class="bg-white dark:bg-gray-900 w-full max-w-lg rounded-t-[20px] shadow-2xl pointer-events-auto flex flex-col max-h-[90vh] overflow-hidden">
        <!-- Grabber -->
        <div class="flex justify-center py-3">
          <div class="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
        </div>

        <div class="px-6 pb-6 overflow-y-auto" :style="{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }">
          <div class="flex justify-between items-start mb-4">
            <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {{ $t('consent.title') }}
            </h2>
          </div>

          <p class="text-gray-600 dark:text-gray-300 text-base mb-8 leading-relaxed">
            {{ $t('consent.description') }}
          </p>

          <!-- Action Buttons Stacked -->
          <div class="flex flex-col space-y-3">
            <button
                class="w-full px-6 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                @click="acceptAll"
            >
              {{ $t('consent.accept_all') }}
            </button>
            <button
                class="w-full px-6 py-4 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600"
                @click="rejectAll"
            >
              {{ $t('consent.use_essential_only') }}
            </button>
            <button
                class="w-full px-6 py-4 text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors focus:outline-none"
                @click="toggleCustom"
            >
              {{ showCustom ? '↑ ' + $t('consent.close_banner') : $t('consent.customize_preferences') }}
            </button>
          </div>

          <!-- Customization Section -->
          <transition name="expand">
            <div v-if="showCustom" id="cookie-options" class="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800 space-y-6">
              <div class="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-xl">
                <p class="text-sm text-blue-800 dark:text-blue-200 leading-snug">
                  <strong>{{ $t('consent.essential_description') }}</strong>
                </p>
              </div>

              <div v-for="category in categories" :key="category.name" class="flex items-center justify-between group">
                <div class="flex-1 pr-4">
                  <p class="font-semibold text-gray-900 dark:text-gray-100 capitalize">{{ $t(category.labelKey) }}</p>
                  <p class="text-sm text-gray-500 dark:text-gray-400 leading-normal">{{ $t(category.descriptionKey) }}</p>
                </div>
                <label :for="`toggle-${category.name}`" class="inline-flex items-center cursor-pointer">
                  <input
                      :id="`toggle-${category.name}`"
                      v-model="prefs[category.name]"
                      :aria-label="$t('consent.toggle_cookies', { category: $t(category.labelKey) })"
                      class="sr-only"
                      type="checkbox"
                  />
                  <div
                      :class="prefs[category.name] ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'"
                      class="w-12 h-7 rounded-full relative transition-colors duration-200 ease-in-out"
                  >
                    <div
                        :class="prefs[category.name] ? 'translate-x-6' : 'translate-x-1'"
                        class="absolute top-1 w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-200 ease-in-out"
                    ></div>
                  </div>
                </label>
              </div>

              <div class="pt-2">
                <button
                    class="w-full px-6 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all"
                    @click="saveCustom"
                >
                  {{ $t('consent.save_preferences') }}
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useConsentStore } from '@/stores/consent'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const store = useConsentStore()
const showCustom = ref(false)
const prefs = ref({ analytics: false, marketing: false })

const show = computed(() => store.loaded && store.showBanner)
const categories = [{
  name: 'analytics',
  labelKey: 'consent.categories.analytics',
  descriptionKey: 'consent.categories.analytics_description',
}, {
  name: 'marketing',
  labelKey: 'consent.categories.marketing',
  descriptionKey: 'consent.categories.marketing_description',
},]

onMounted(async () => {
  if (store.showBanner) {
    prefs.value = {
      analytics: false, marketing: false,
    }
  } else {
    prefs.value = {
      analytics: store.consent.analytics, marketing: store.consent.marketing,
    }
  }
})

function acceptAll () {
  prefs.value = { analytics: true, marketing: true }
  store.acceptAll('banner')
}

function rejectAll () {
  prefs.value = { analytics: false, marketing: false }
  store.rejectAll('banner')
}

function toggleCustom () {
  showCustom.value = !showCustom.value
}

function saveCustom () {
  store.saveCustom(prefs.value, 'modal')
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
}
.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
}

.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease-out;
  max-height: 500px;
  overflow: hidden;
}
.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Custom switch shadows */
label div div {
  box-shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06);
}
</style>

